import { User, WarningCircle } from '@phosphor-icons/react';
import { MilesChatMessage } from './milesTypes';
import MilesStructuredData from './MilesStructuredData';
import MilesMarkdown from './MilesMarkdown';
import MilesAvatar from './MilesAvatar';
import MilesThinkingIndicator from './MilesThinkingIndicator';

interface MilesMessageListProps {
  messages: MilesChatMessage[];
}

/**
 * Renders the list of chat messages in the MILES chat window.
 * Handles user messages, assistant messages, loading states, and errors.
 */
function MilesMessageList({ messages }: MilesMessageListProps) {
  return (
    <div className="space-y-4">
      {messages.map((message) => (
        <MessageBubble key={message.message_id} message={message} />
      ))}
    </div>
  );
}

interface MessageBubbleProps {
  message: MilesChatMessage;
}

function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const isProcessing = message.status === 'processing';
  const isFailed = message.status === 'failed';

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div className="flex-shrink-0">
        {isUser ? (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200">
            <User size={18} className="text-gray-600" />
          </div>
        ) : (
          <MilesAvatar
            size="sm"
            animate={isProcessing}
            loop={isProcessing}
            useStaticFallback={!isProcessing}
          />
        )}
      </div>

      {/* Message Content - flex column to allow bubbles to shrink to content */}
      <div
        className={`flex max-w-[80%] flex-col space-y-1 ${isUser ? 'items-end' : 'items-start'}`}
      >
        {/* Message Bubble - w-fit ensures it shrinks to content */}
        <div
          className={`w-fit max-w-full rounded-2xl px-3 py-2 ${
            isUser
              ? 'rounded-br-md bg-primary text-white'
              : 'rounded-bl-md bg-gray-100 text-gray-900'
          } ${isFailed ? 'bg-red-50 text-red-600' : ''}`}
        >
          {isProcessing ? (
            <MilesThinkingIndicator variant="inline" message="Thinking" />
          ) : isFailed ? (
            <div className="flex items-center gap-2">
              <WarningCircle size={16} />
              <span className="text-[15px]">
                {message.content || 'An error occurred'}
              </span>
            </div>
          ) : isUser ? (
            <div className="whitespace-pre-wrap text-[15px]">
              {message.content}
            </div>
          ) : (
            <div className="text-[15px]">
              <MilesMarkdown content={message.content} />
            </div>
          )}
        </div>

        {/* Structured Data (charts, tables, etc.) */}
        {!isUser &&
          message.status === 'completed' &&
          message.structured_data &&
          message.structured_data.length > 0 && (
            <div className="mt-2 w-full space-y-3">
              {message.structured_data.map((block, index) => (
                <MilesStructuredData key={index} block={block} />
              ))}
            </div>
          )}

        {/* Timestamp */}
        <p className="text-[13px] text-gray-400">
          {formatTime(message.created_at)}
        </p>
      </div>
    </div>
  );
}

function formatTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default MilesMessageList;
